import React, { useEffect, useRef, useState, useCallback } from 'react'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { Input, Toast } from 'antd-mobile'
import {
  rocketChatLogin,
  getChatThreadMessages,
  getRocketChatMessageById,
  deleteRocketChatMessage,
  pinRocketChatMessage,
  unpinRocketChatMessage,
  uploadFile,
  sendMessageNotification,
  updateRocketChatMessage,
  getRocketChatRooms,
  getMe
} from '../../api'
import { toBase64, setTokenItem, saveUser, loadUser, getToken, getCachedRoomsData, setCachedRoomsData } from '../../utils'
import ThreadView from './ThreadView'
import './styles/ChannelMessages.css'
import IosSpinner from './IosSpinner'

const ThreadPage = () => {
  const { channelId, threadId } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const [selectedThread, setSelectedThread] = useState(null)
  const [replies, setReplies] = useState([])
  const [loading, setLoading] = useState(true)
  const [threadInput, setThreadInput] = useState('')
  const [threadFilteredUsers, setThreadFilteredUsers] = useState([])
  const [showThreadUserSuggestions, setShowThreadUserSuggestions] = useState(false)
  const [editingThreadMessage, setEditingThreadMessage] = useState(null)
  const [jumpToMessageId, setJumpToMessageId] = useState(null)
  const [users, setUsers] = useState([])
  const [userAvatarsState, setUserAvatarsState] = useState([])

  const ws = useRef(null)
  const isFetching = useRef(false)
  const messageQueue = useRef([])

  const [rocketChatData, setRocketChatData] = useState(() => {
    const data = localStorage.getItem('rocket-chat-data')
    return data ? JSON.parse(data) : null
  })

  const [person, setPerson] = useState(() => loadUser())

  const refreshPerson = async (token) => {
    setTokenItem(token)
    const { person: newPerson } = await getMe()
    setPerson(newPerson)
    saveUser(newPerson)

    const rocketData = await rocketChatLogin(newPerson.id)
    setRocketChatData(rocketData)
    localStorage.setItem('rocket-chat-data', JSON.stringify(rocketData))
  }

  useEffect(() => {
    fetchData()
  }, [threadId, person])

  useEffect(() => {
    window.handleMessageFromNative = refreshPerson

    if (navigator.userAgent.includes('life.apeiron.ios')) {
      if (!getToken() || !loadUser() || !rocketChatData) {
        try {
          window.webkit?.messageHandlers?.loadUser?.postMessage({})
        } catch (err) {
          console.error('🚨 Failed to request token from Swift in ThreadPage:', err)
        }
      }
    }
  }, [])

  useEffect(() => {
    if (channelId && rocketChatData) {
      connectWebSocket()
    }
  }, [channelId, rocketChatData])

  const fetchData = async () => {
    if (!person?.id) return
    if (isFetching.current) {
      return
    }
    isFetching.current = true
    setLoading(true)
    try {
      // Fetch the root message
      const res = await getRocketChatMessageById({
        messageId: threadId,
        personId: person.id
      })
      if (res.message) {
        setSelectedThread(res.message)
        // Fetch replies
        const repliesRes = await getChatThreadMessages({
          roomId: res.message.rid,
          personId: person.id,
          threadId: threadId
        })
        setReplies(repliesRes.messages || [])

        // Fetch channel for users (for mentions)
        // Check for cached rooms data first
        let roomsRes = getCachedRoomsData()

        // Fetch fresh data if no valid cache
        if (!roomsRes) {
          roomsRes = await getRocketChatRooms({
            person_id: person.id,
            client_id: person.id
          })

          // Cache the rooms data
          setCachedRoomsData(roomsRes)
        }

        const channel = roomsRes.rooms.find(r => r._id === res.message.rid)

        if (channel) {
          setUsers(channel.users || [])
          setUserAvatarsState(roomsRes?.userAvatars || [])
        }
      }
    } catch (err) {
      Toast.show({
        content: 'Failed to load thread',
        icon: 'fail',
        duration: 3000
      })
    } finally {
      setLoading(false)
    }
  }

  const connectWebSocket = () => {
    if (ws.current && ws.current.readyState === WebSocket.OPEN) {
      return
    }
    ws.current = new WebSocket(process.env.REACT_APP_ROCKET_CHAT_WS_URL)

    ws.current.onopen = () => {
      if (ws.current.readyState === WebSocket.OPEN) {
        ws.current.send(JSON.stringify({ msg: 'connect', version: '1', support: ['1'] }))
      } else {
        console.warn('[WS] Connection not fully open, readyState:', ws.current.readyState)
      }
    }

    ws.current.onmessage = async (event) => {
      const data = JSON.parse(event.data)

      if (data.msg === 'connected') {
        ws.current.send(
          JSON.stringify({
            msg: 'method',
            method: 'login',
            id: '1',
            params: [{ resume: rocketChatData.authToken }]
          })
        )
      }

      if (data.msg === 'ping') {
        ws.current.send(JSON.stringify({ msg: 'pong' }))
      }

      if (data.msg === 'result' && data.id === '1') {
        if (data.error) {
          console.warn('Login failed in ThreadPage:', data.error)
          const newData = await rocketChatLogin(person.id)
          localStorage.setItem('rocket-chat-data', JSON.stringify(newData))
          rocketChatData.authToken = newData.authToken
          ws.current.send(
            JSON.stringify({
              msg: 'method',
              method: 'login',
              id: '1',
              params: [{ resume: rocketChatData.authToken }]
            })
          )
          return
        }

        ws.current.send(
          JSON.stringify({
            msg: 'sub',
            id: '2',
            name: 'stream-room-messages',
            params: [channelId, false]
          })
        )
        flushMessageQueue()
      }

      if (data.msg === 'changed' && data.collection === 'stream-room-messages') {
        const msg = data.fields.args[0]
        msg['ts'] = msg['ts']['$date']

        if (msg.tmid === threadId) {
          setReplies(prev => {
            const exists = prev.find(m => m._id === msg._id)
            return exists
              ? prev.map(m => (m._id === msg._id ? { ...m, ...msg } : m))
              : [...prev, msg]
          })
        }
      }
    }

    ws.current.onclose = (e) => {
      console.warn('[WS] Connection closed. Reason:', e.reason)
    }

    ws.current.onerror = (e) => {
      console.error('[WS] WebSocket error', e)
    }
  }

  const flushMessageQueue = () => {
    while (messageQueue.current.length > 0 && ws.current.readyState === WebSocket.OPEN) {
      const msg = messageQueue.current.shift()
      ws.current.send(JSON.stringify(msg))
    }
  }

  const handleSendReply = async (text) => {
    if (!text.trim()) return
    setThreadInput('')

    if (editingThreadMessage) {
      try {
        // For editing, send the raw text as entered (which already has display names)
        const res = await updateRocketChatMessage({
          message_id: editingThreadMessage._id,
          room_id: channelId,
          person_id: person.id,
          new_text: text.trim()
        })
        if (res.success) {
          Toast.show({ content: 'Message updated successfully', icon: 'success' })
          setReplies(prev =>
            prev.map(m =>
              m._id === editingThreadMessage._id ? { ...m, msg: text.trim() } : m
            )
          )
        } else {
          Toast.show({ content: 'Update failed', icon: 'fail' })
        }
      } catch (err) {
        Toast.show({ content: 'Update failed', icon: 'fail' })
      }
      setEditingThreadMessage(null)
      return
    }

    const msgObj = {
      msg: 'method',
      method: 'sendMessage',
      id: `${Date.now()}`,
      params: [
        {
          rid: channelId,
          msg: text.trim(),
          tmid: threadId
        }
      ]
    }

    if (ws.current && ws.current.readyState === WebSocket.OPEN) {
      ws.current.send(JSON.stringify(msgObj))
    } else {
      Toast.show({
        content: 'Connection lost. Please reload the app.',
        icon: 'fail',
        duration: 3000
      })
      messageQueue.current.push(msgObj)
    }

    try {
      await sendMessageNotification({
        client_id: person.id,
        text: text.trim()
      })
    } catch (err) {
      Toast.show({ content: 'Notification failed', icon: 'fail' })
    }
  }

  const handleImageUpload = async (file) => {
    try {
      let imageUrl

      if (file.type === 'image/svg+xml') {
        imageUrl = await toBase64(file)
      } else {
        const res = await uploadFile({ personId: person.id, file })
        imageUrl = res.url || res.data?.url
      }

      const imageHtml = `<p><img class="ql-image" src="${imageUrl}" /></p>`
      await handleSendReply(imageHtml)
    } catch (err) {
      Toast.show({ content: 'Image upload failed', icon: 'fail' })
    }
  }

  const handleEditMessage = (msg) => {
    const editedText = replaceUsernamesWithNames(msg.msg, users)
    setEditingThreadMessage(msg)
    setThreadInput(editedText)
  }

  const handleDeleteMessage = async ({ messageId, roomId }) => {
    try {
      const res = await deleteRocketChatMessage({
        messageId,
        roomId,
        personId: person.id
      })
      if (res.success) {
        Toast.show({ content: 'Message deleted', icon: 'success' })
        setReplies(prev => prev.filter(msg => msg._id !== messageId))
      } else {
        Toast.show({ content: res.data.message || 'Delete failed', icon: 'fail' })
      }
    } catch (err) {
      Toast.show({ content: err?.response?.data?.message || 'Delete failed', icon: 'fail' })
    }
  }

  const handlePinMessage = async (msg, pinned) => {
    try {
      const pinOrUnpinMessages = pinned ? unpinRocketChatMessage : pinRocketChatMessage

      const res = await pinOrUnpinMessages({
        message_id: msg._id,
        person_id: person.id
      })

      if (res.success) {
        Toast.show({
          content: pinned ? 'Message unpinned' : 'Message pinned',
          icon: 'success'
        })

        setReplies(prev =>
          prev.map(m =>
            m._id === msg._id ? { ...m, pinned: !pinned } : m
          )
        )
      } else {
        Toast.show({ content: 'Action failed', icon: 'fail' })
      }
    } catch (err) {
      Toast.show({ content: 'Action failed', icon: 'fail' })
    }
  }

  const handleMentionInputChange = (val, setInputFn, setFilteredUsersFn, setShowSuggestionsFn) => {
    setInputFn(val)
    const atIndex = val.lastIndexOf('@')
    if (atIndex !== -1) {
      let afterAt = val.slice(atIndex + 1)
      if (afterAt.includes(' ')) {
        setShowSuggestionsFn(false)
        return
      }
      const originalAfterAt = afterAt  // Store original before splitting
      afterAt = afterAt.split(/\s/)[0]
      const query = afterAt.toLowerCase()
      const exactMatch = users.some(u =>
        u.username?.toLowerCase() === query || u.name?.toLowerCase() === query
      )
      if (exactMatch && !originalAfterAt.includes(' ')) {
        setShowSuggestionsFn(false)
        return
      }
      if (query.length === 0) {
        setFilteredUsersFn(users)
        setShowSuggestionsFn(true)
        return
      }
      const filtered = users.filter(u =>
        u.name?.toLowerCase().includes(query) ||
        u.username?.toLowerCase().includes(query)
      )
      setFilteredUsersFn(filtered)
      setShowSuggestionsFn(filtered.length > 0)
    } else {
      setShowSuggestionsFn(false)
    }
  }

  function replaceMentionsWithUsernames(text, usersList) {
    let newText = text
    usersList.forEach(user => {
      const nameRegex = new RegExp(`@${user.name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}(?=\\s|$)`, 'g')
      newText = newText.replace(nameRegex, `@${user.username}`)
    })
    return newText
  }

  function replaceUsernamesWithNames(text, usersList) {
    let newText = text
    usersList.forEach(user => {
      const usernameRegex = new RegExp(`@${user.username.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}(?=\\s|$)`, 'g')
      newText = newText.replace(usernameRegex, `@${user.name}`)
    })
    return newText
  }

  const handleMentionUserSelect = (user, inputVal, setInputFn, setShowSuggestionsFn) => {
    const val = inputVal
    const atIndex = val.lastIndexOf('@')
    if (atIndex !== -1) {
      const beforeAt = val.slice(0, atIndex)
      const afterAt = val.slice(atIndex + 1)
      const parts = afterAt.split(/\s(.+)/)
      const restOfSentence = parts[1] || ''
      const newVal = `${beforeAt}@${user.name} ${restOfSentence}`
      setInputFn(newVal)
      setShowSuggestionsFn(false)
    }
  }

  const handleThreadInputChangeFn = (val) => {
    handleMentionInputChange(val, setThreadInput, setThreadFilteredUsers, setShowThreadUserSuggestions)
  }

  const handleThreadUserSelect = (user) => {
    handleMentionUserSelect(user, threadInput, setThreadInput, setShowThreadUserSuggestions)
  }

  const handleBack = () => {
    navigate('/chat/' + channelId, { state: location.state })
  }

  if (loading) return <IosSpinner />

  return (
    <ThreadView
      selectedThread={selectedThread}
      replies={replies}
      users={users}
      userAvatars={userAvatarsState}
      onBack={handleBack}
      threadInput={threadInput}
      setThreadInput={setThreadInput}
      onSendReply={() => handleSendReply(replaceMentionsWithUsernames(threadInput, users))}
      onDeleteMessage={handleDeleteMessage}
      onEditMessage={handleEditMessage}
      onPinMessage={handlePinMessage}
      currentUserId={rocketChatData?.userId}
      isEditing={!!editingThreadMessage}
      editingMessageId={editingThreadMessage?._id}
      onCancelEdit={() => {
        setEditingThreadMessage(null)
        setThreadInput('')
        setShowThreadUserSuggestions(false)
      }}
      handleThreadInputChange={handleThreadInputChangeFn}
      showThreadUserSuggestions={showThreadUserSuggestions}
      threadFilteredUsers={threadFilteredUsers}
      handleThreadUserSelect={handleThreadUserSelect}
      onImageUpload={handleImageUpload}
      jumpToMessageId={jumpToMessageId}
    />
  )
}

export default ThreadPage
